import React, { useMemo, useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import { formatINR, whatsappLink } from "@/lib/api";
import { MessageCircle, Calculator } from "lucide-react";

export default function MakingChargeCalculator({
    defaultPurity = "22K",
    defaultWeight = 10,
    defaultMakingPct = 8,
    productName,
}) {
    const { rates, settings } = useSettings();
    const [purity, setPurity] = useState(defaultPurity);
    const [weight, setWeight] = useState(defaultWeight);
    const [makingPct, setMakingPct] = useState(defaultMakingPct);
    const [gstOn, setGstOn] = useState(true);

    const ratePerGram = useMemo(() => {
        if (!rates) return 0;
        if (purity === "24K") return rates.gold_24k || 0;
        if (purity === "22K") return rates.gold_22k || 0;
        if (purity === "20K") return rates.gold_20k || 0;
        if (purity === "18K") return rates.gold_18k || 0;
        return 0;
    }, [rates, purity]);

    const goldValue = ratePerGram * (Number(weight) || 0);
    const making = goldValue * (Number(makingPct) || 0) / 100;
    const subtotal = goldValue + making;
    const gst = gstOn ? subtotal * 0.03 : 0;
    const total = subtotal + gst;

    const wa = whatsappLink(
        settings.whatsapp_number,
        `Hello Singh Jewellers,\n\nI used your calculator for${productName ? ` ${productName} —` : ""}:\nPurity: ${purity}\nWeight: ${weight} g\nRate: ₹${ratePerGram}/g\nMaking: ${makingPct}%\nEstimated Total: ${formatINR(Math.round(total))}\n\nPlease share the final quote.`
    );

    return (
        <div className="card-luxe p-6 md:p-8" data-testid="making-charge-calculator">
            <div className="flex items-center gap-3 mb-5">
                <Calculator className="text-[color:var(--champagne)]" size={22} />
                <div>
                    <div className="eyebrow">Honest Pricing</div>
                    <h3 className="font-serif-luxe text-2xl mt-1">Making Charge Calculator</h3>
                </div>
            </div>
            <p className="text-xs text-[color:var(--muted-brown)] mb-6">
                See the honest price before you visit — gold value + making charge, transparent to the last rupee.
            </p>

            <div className="grid md:grid-cols-2 gap-5 mb-6">
                <div>
                    <label className="text-xs tracking-[0.18em] uppercase text-[color:var(--muted-brown)]">Purity</label>
                    <div className="grid grid-cols-4 gap-2 mt-2">
                        {["24K","22K","20K","18K"].map(p => (
                            <button key={p} onClick={() => setPurity(p)} data-testid={`calc-purity-${p}`}
                                className={`py-2 text-sm border transition-colors ${purity === p ? "bg-[color:var(--charcoal)] text-white border-[color:var(--charcoal)]" : "border-[color:var(--soft-border)] text-[color:var(--charcoal)] hover:border-[color:var(--champagne)]"}`}>
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="text-xs tracking-[0.18em] uppercase text-[color:var(--muted-brown)]">Rate per gram</label>
                    <div className="mt-2 border border-[color:var(--soft-border)] px-3 py-2 bg-[color:var(--beige)]/40 font-serif-luxe text-lg" data-testid="calc-rate">
                        ₹ {Number(ratePerGram).toLocaleString("en-IN")}
                    </div>
                </div>
                <div>
                    <label className="text-xs tracking-[0.18em] uppercase text-[color:var(--muted-brown)]">Weight (grams)</label>
                    <input type="number" min="0" step="0.1" value={weight} onChange={e => setWeight(e.target.value)} data-testid="calc-weight"
                        className="w-full mt-2 border border-[color:var(--soft-border)] focus:border-[color:var(--champagne)] outline-none px-3 py-2 bg-transparent" />
                </div>
                <div>
                    <label className="text-xs tracking-[0.18em] uppercase text-[color:var(--muted-brown)]">Making Charge (%)</label>
                    <input type="number" min="0" max="30" step="0.5" value={makingPct} onChange={e => setMakingPct(e.target.value)} data-testid="calc-making"
                        className="w-full mt-2 border border-[color:var(--soft-border)] focus:border-[color:var(--champagne)] outline-none px-3 py-2 bg-transparent" />
                </div>
            </div>

            <label className="flex items-center gap-2 text-xs text-[color:var(--muted-brown)] mb-5">
                <input type="checkbox" checked={gstOn} onChange={e => setGstOn(e.target.checked)} data-testid="calc-gst" /> Include 3% GST
            </label>

            <div className="divider-line mb-5" />

            <div className="space-y-2 text-sm mb-6">
                <Row label="Gold Value" value={formatINR(Math.round(goldValue))} testid="calc-gold-value" />
                <Row label={`Making Charge (${makingPct}%)`} value={formatINR(Math.round(making))} testid="calc-making-value" />
                {gstOn && <Row label="GST (3%)" value={formatINR(Math.round(gst))} testid="calc-gst-value" />}
                <div className="pt-3 border-t border-[color:var(--soft-border)] flex items-end justify-between">
                    <span className="eyebrow">Estimated Total</span>
                    <span className="font-serif-luxe text-3xl text-[color:var(--charcoal)]" data-testid="calc-total">{formatINR(Math.round(total))}</span>
                </div>
            </div>

            <a href={wa} target="_blank" rel="noreferrer" className="btn-whatsapp w-full" data-testid="calc-whatsapp">
                <MessageCircle size={16} /> Get Final Quote on WhatsApp
            </a>
            <p className="text-[10px] text-[color:var(--muted-brown)] mt-3 leading-relaxed text-center">
                Indicative estimate. Final price varies with day's rate, purity, hallmarking and design. Please confirm at the showroom.
            </p>
        </div>
    );
}

function Row({ label, value, testid }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-[color:var(--muted-brown)]">{label}</span>
            <span className="text-[color:var(--charcoal)] font-medium" data-testid={testid}>{value}</span>
        </div>
    );
}
